import { useState } from 'react';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { collections as mockCollections } from '../../../mocks/altaCostura';
import { useSupabaseCollections } from '../../../hooks/useSupabaseCollections';

export default function GallerySection() {
  const { ref, isVisible, style } = useScrollReveal({ threshold: 0.1, distance: 36, duration: 900 });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(4);

  const { data: collections } = useSupabaseCollections(
    mockCollections.map((c, i) => ({ ...c, sort_order: i }))
  );

  const selected = collections.find((c) => c.id === selectedId);
  const displayed = collections.slice(0, visibleCount);
  const hasMore = visibleCount < collections.length;

  const openLightbox = (id: number, imgIndex = 0) => {
    setSelectedId(id);
    setLightboxIndex(imgIndex);
  };

  const closeLightbox = () => {
    setSelectedId(null);
    setLightboxIndex(0);
  };

  const nextImage = () => {
    if (!selected) return;
    setLightboxIndex((i) => (i + 1) % selected.images.length);
  };

  const prevImage = () => {
    if (!selected) return;
    setLightboxIndex((i) => (i - 1 + selected.images.length) % selected.images.length);
  };

  const handleShowMore = () => {
    setVisibleCount((prev) => Math.min(prev + 4, collections.length));
  };

  return (
    <section ref={ref} className="w-full bg-white py-20 md:py-32">
      <div className="px-6 lg:px-[7%]">
        {/* Header */}
        <div style={style}>
          <span className="font-sans text-brand-gray-400 text-[11px] tracking-[0.2em] uppercase font-medium">
            Colecciones
          </span>
          <h2 className="font-serif text-brand-black text-[clamp(2.5rem,5vw,4rem)] font-semibold leading-[1.1] mt-4">
            Portafolio de
            <br />
            <span className="italic text-brand-gray-300">Alta Costura</span>
          </h2>
          <p className="font-sans text-brand-gray-500 text-[15px] leading-[1.8] mt-6 max-w-xl">
            Cada colección es un universo propio donde la artesanía, la emoción y la dedicación
            se funden en prendas irrepetibles. Explora nuestras creaciones más recientes.
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {displayed.map((col, index) => (
            <div
              key={col.id}
              className="group cursor-pointer"
              style={isVisible ? {
                opacity: 1,
                transform: 'translateY(0px)',
                transition: `opacity 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${200 + index * 140}ms, transform 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${200 + index * 140}ms`,
              } : {
                opacity: 0,
                transform: 'translateY(36px)',
                transition: `opacity 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${200 + index * 140}ms, transform 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${200 + index * 140}ms`,
                willChange: 'opacity, transform',
              }}
              onClick={() => openLightbox(col.id)}
            >
              <div className="relative aspect-[3/4] rounded-[28px] overflow-hidden border border-transparent group-hover:border-brand-gray-100 transition-colors duration-500">
                <img
                  src={col.image}
                  alt={col.name}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-500" />
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-center gap-2">
                  <span className="font-sans text-white/80 text-xs tracking-wider uppercase">
                    Ver colección
                  </span>
                  <i className="ri-arrow-right-line text-white/80 text-xs" />
                </div>
              </div>
              <div className="mt-5 flex items-start justify-between">
                <div>
                  <h3 className="font-serif text-brand-black text-xl md:text-2xl font-medium group-hover:text-brand-gray-500 transition-colors duration-300">
                    {col.name}
                  </h3>
                  <p className="font-sans text-brand-gray-500 text-sm mt-2 leading-relaxed max-w-sm">
                    {col.description}
                  </p>
                </div>
                <span className="font-sans text-brand-gray-400 text-sm font-medium">
                  {col.year}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Show More Button */}
        {hasMore && (
          <div className="mt-12 flex justify-center">
            <button
              onClick={handleShowMore}
              className="inline-flex items-center gap-2 bg-brand-black text-white font-sans text-[13px] tracking-widest uppercase px-8 py-4 rounded-full hover:bg-brand-gray-600 transition-colors duration-300 whitespace-nowrap"
            >
              Ver más colecciones
              <i className="ri-arrow-down-line" />
            </button>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {selected && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-12"
          onClick={closeLightbox}
        >
          <div
            className="relative max-w-4xl max-h-[90vh] w-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeLightbox}
              className="absolute -top-12 right-0 w-10 h-10 flex items-center justify-center text-white/70 hover:text-white transition-colors z-10"
              aria-label="Cerrar"
            >
              <i className="ri-close-line text-2xl" />
            </button>

            <div className="relative w-full h-[70vh] max-h-[700px] rounded-2xl overflow-hidden">
              <img
                src={selected.images[lightboxIndex]}
                alt={`${selected.name} - ${lightboxIndex + 1}`}
                className="w-full h-full object-contain"
              />
            </div>

            {/* Thumbnails */}
            <div className="flex gap-3 mt-6">
              {selected.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setLightboxIndex(i)}
                  className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                    i === lightboxIndex ? 'border-white' : 'border-transparent'
                  }`}
                >
                  <img src={img} alt={`Thumbnail ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>

            {/* Controls */}
            {selected.images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-0 md:-left-16 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-white/30 flex items-center justify-center text-white hover:bg-white/10 transition-all"
                  aria-label="Imagen anterior"
                >
                  <i className="ri-arrow-left-line" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-0 md:-right-16 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-white/30 flex items-center justify-center text-white hover:bg-white/10 transition-all"
                  aria-label="Siguiente imagen"
                >
                  <i className="ri-arrow-right-line" />
                </button>
              </>
            )}

            <div className="mt-4 text-center">
              <h3 className="font-serif text-white text-xl font-medium">{selected.name}</h3>
              <span className="font-sans text-white/50 text-sm">{selected.year}</span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
